<!--
  @component TokensIO — user-scoped API token surface.
  chain:  ctx.deployment.dispatch("tokens.*", …) → this → ctx.tokens
  Distinct from CredentialsIO (which is rotate-as-a-deploy for
  provider secrets). TokensIO manages the user's own `lqd_<env>_…`
  Bearer tokens. Reveal-once UX lives in the consumer panel; this
  portal just shuttles dispatches through the parent.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { TOKENS_IO_KEY } from "./deployment-io.js";
  import type {
    TokensIOContext,
    TokensCreateInput,
    TokensRowPublic,
    TokensRendered,
    TokensUseEntry,
  } from "./deployment-io.js";
  export { TOKENS_IO_KEY };
  export type {
    TokensIOContext,
    TokensCreateInput,
    TokensRowPublic,
    TokensRendered,
    TokensUseEntry,
  };
  export function useTokensIO(): TokensIOContext {
    const ctx = getContext<TokensIOContext | undefined>(TOKENS_IO_KEY);
    if (!ctx) throw new Error("useTokensIO() called outside of a TokensIO tree.");
    return ctx;
  }
</script>
<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchOp<T>(op: string, payload: unknown): Promise<T> {
    inFlight = true; lastError = null;
    try {
      const r = (await parent.dispatch(op, payload)) as T;
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally { inFlight = false; }
  }

  const ctx: TokensIOContext = {
    get inFlight() { return inFlight; },
    get lastResult() { return lastResult; },
    get lastError() { return lastError; },
    get history() {
      return parent.stream.filter((e) => e.op.startsWith("tokens."));
    },
    list: () => dispatchOp<{ tokens: readonly TokensRowPublic[] }>("tokens.list", {}),
    create: (input: TokensCreateInput) => dispatchOp<TokensRendered>("tokens.create", input),
    revoke: (tokenId: string, reason?: string) =>
      dispatchOp<{ tokenId: string; revoked: boolean }>("tokens.revoke", { tokenId, reason }),
    rotate: (tokenId: string) =>
      dispatchOp<TokensRendered & { oldTokenId: string; newTokenId: string }>(
        "tokens.rotate",
        { tokenId },
      ),
    uses: (tokenId: string, limit?: number) =>
      dispatchOp<{ tokenId: string; uses: readonly TokensUseEntry[] }>(
        "tokens.uses",
        { tokenId, limit },
      ),
    stats: (tokenId: string) =>
      dispatchOp<{
        tokenId: string;
        total: number;
        allowed: number;
        denied: number;
        distinctIps: number;
        topOps: readonly { op: string; count: number }[];
      }>("tokens.stats", { tokenId }),
  } satisfies TokensIOContext;

  setContext(TOKENS_IO_KEY, ctx);
</script>
{@render children?.()}
